<template>
  <div
    class="login-canvas min-h-screen flex items-center justify-center px-6 py-12 font-sans antialiased"
  >
    <!-- Decorative watermark icons (same technique as the dashboard cards) -->
    <i class="wm wm-1 material-icons-round">local_taxi</i>
    <i class="wm wm-2 material-icons-round">navigation</i>
    <i class="wm wm-3 material-icons-round">directions_car</i>
    <i class="wm wm-4 material-icons-round">place</i>
    <i class="wm wm-5 material-icons-round">local_taxi</i>

    <!-- Login card -->
    <div
      class="login-card relative w-full max-w-md bg-white rounded-3xl px-8 py-10 sm:px-10"
    >
      <i class="card-watermark material-icons-round">local_taxi</i>

      <div class="relative flex flex-col items-center text-center">
        <div class="brand-logo flex items-center justify-center">
          <img
            src="@/assets/logo.png"
            alt="Taxiye"
            class="h-16 w-16 rounded-full"
          />
        </div>
        <h1 class="mt-5 text-2xl font-bold text-gray-900">Taxiye Admin</h1>
        <p class="mt-1 text-sm text-gray-400">Sign in to continue</p>
      </div>

      <form @submit.prevent="login" class="relative mt-8">
        <transition name="fade">
          <div
            v-if="error"
            class="mb-5 flex items-center rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
          >
            <span class="mr-2 text-lg leading-none material-icons-round"
              >error_outline</span
            >
            <span>Invalid email or password.</span>
          </div>
        </transition>

        <div class="input-wrap relative">
          <span class="input-icon material-icons-round">mail_outline</span>
          <input
            id="email"
            required
            v-model="email"
            type="email"
            autocomplete="email"
            placeholder="Email address"
            class="login-input block w-full pl-12 pr-3 py-3 rounded-xl"
          />
        </div>

        <div class="input-wrap relative mt-4">
          <span class="input-icon material-icons-round">lock_outline</span>
          <input
            id="password"
            required
            v-model="password"
            :type="showPassword ? 'text' : 'password'"
            autocomplete="current-password"
            placeholder="Password"
            class="login-input block w-full pl-12 pr-12 py-3 rounded-xl"
          />
          <button
            type="button"
            tabindex="-1"
            @click="showPassword = !showPassword"
            class="input-toggle absolute inset-y-0 right-0 flex items-center px-4 focus:outline-none"
            :aria-label="showPassword ? 'Hide password' : 'Show password'"
          >
            <span class="material-icons-round text-xl">
              {{ showPassword ? "visibility_off" : "visibility" }}
            </span>
          </button>
        </div>

        <button
          :disabled="busy"
          type="submit"
          class="login-btn mt-7 w-full flex items-center justify-center px-4 py-3 text-sm font-semibold text-white rounded-xl focus:outline-none"
          :class="{ 'is-busy': busy }"
        >
          <svg
            v-if="busy"
            class="animate-spin h-4 w-4 mr-2 text-white"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              class="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              stroke-width="4"
            ></circle>
            <path
              class="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
          {{ busy ? "Signing in..." : "Sign in" }}
        </button>
      </form>
    </div>
  </div>
</template>

<script>
export default {
  data() {
    return {
      email: "",
      password: "",
      busy: false,
      error: false,
      showPassword: false,
    };
  },
  methods: {
    login() {
      this.busy = true;
      this.error = false;
      const user = {
        email: this.email,
        password: this.password,
      };

      this.$store
        .dispatch("login", user)
        .then(() => {
          console.log("after login");

          window.flash({ type: "success", body: "Logged in successfully!" });
          this.$router.push("/role");
        })
        .catch(() => {
          this.error = true;
        })
        .finally(() => (this.busy = false));
    },
  },
};
</script>

<style scoped>
/* ── Canvas — warm branded backdrop (matches dash-canvas) ── */
.login-canvas {
  position: relative;
  overflow: hidden;
  background: radial-gradient(
      120% 90% at 100% 0%,
      rgba(238, 75, 43, 0.1) 0%,
      transparent 45%
    ),
    radial-gradient(
      100% 90% at 0% 100%,
      rgba(255, 215, 0, 0.14) 0%,
      transparent 50%
    ),
    linear-gradient(180deg, #fffbf2 0%, #f4f3ef 100%);
}

/* Oversized faded watermark icons — same effect as the dashboard cards */
.wm {
  position: absolute;
  color: #ee4b2b;
  opacity: 0.06;
  pointer-events: none;
  user-select: none;
  line-height: 1;
}
.wm-1 {
  top: 6%;
  left: 8%;
  font-size: 9rem;
  transform: rotate(-15deg);
}
.wm-2 {
  top: 14%;
  right: 10%;
  font-size: 7rem;
  color: #ffd700;
  opacity: 0.16;
  transform: rotate(20deg);
}
.wm-3 {
  bottom: 10%;
  left: 12%;
  font-size: 8rem;
  transform: rotate(10deg);
}
.wm-4 {
  bottom: 16%;
  right: 14%;
  font-size: 6rem;
  color: #ffd700;
  opacity: 0.18;
  transform: rotate(-12deg);
}
.wm-5 {
  top: 45%;
  left: 46%;
  font-size: 12rem;
  opacity: 0.04;
  transform: rotate(-8deg);
}

/* ── Card ─────────────────────────────────────────────────── */
.login-card {
  overflow: hidden;
  border: 1px solid #f0e6cc;
  box-shadow: 0 2px 4px rgba(77, 50, 0, 0.04),
    0 24px 48px -12px rgba(77, 50, 0, 0.22);
}
/* Card's own corner watermark, like the dashboard stat cards */
.card-watermark {
  position: absolute;
  right: -1.5rem;
  bottom: -2rem;
  font-size: 9rem;
  line-height: 1;
  color: #ee4b2b;
  opacity: 0.05;
  transform: rotate(-12deg);
  pointer-events: none;
}

/* Logo with the gold ring used across the app */
.brand-logo {
  border-radius: 9999px;
  box-shadow: 0 0 0 3px rgba(255, 215, 0, 0.55),
    0 6px 16px rgba(77, 50, 0, 0.18);
}

/* ── Inputs — icon prefix + gold focus ring (matches sidebar) ── */
.input-icon {
  position: absolute;
  top: 50%;
  left: 14px;
  transform: translateY(-50%);
  font-size: 20px;
  color: #a0aec0;
  transition: color 0.2s ease;
  pointer-events: none;
}
.login-input {
  color: #2d3748;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}
.login-input::placeholder {
  color: #a0aec0;
}
.login-input:hover {
  border-color: #cbd5e0;
}
.login-input:focus {
  outline: none;
  border-color: #ffd700;
  box-shadow: 0 0 0 3px rgba(255, 215, 0, 0.25);
}
.input-wrap:focus-within .input-icon {
  color: #996300;
}
.input-toggle {
  color: #a0aec0;
  transition: color 0.15s ease;
}
.input-toggle:hover {
  color: #4a5568;
}

/* ── Sign-in button — gold→taxiye gradient with glow ── */
.login-btn {
  background: #ee4b2b;
  box-shadow: 0 4px 14px rgba(238, 75, 43, 0.28);
  transition: box-shadow 0.2s ease, transform 0.05s ease, filter 0.2s ease;
}
.login-btn:hover {
  filter: brightness(1.03);
  box-shadow: 0 6px 20px rgba(238, 75, 43, 0.38);
}
.login-btn:active {
  transform: translateY(1px);
}
.login-btn:focus {
  box-shadow: 0 0 0 3px rgba(255, 215, 0, 0.4),
    0 4px 14px rgba(238, 75, 43, 0.28);
}
.login-btn.is-busy {
  background: #cbb26b;
  box-shadow: none;
  cursor: not-allowed;
}

/* Transitions */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
